/**
 * Green Flag — UnmatchConfirmModal
 *
 * Confirmation modal for the "Unmatch" menu action on /matches/:userId.
 * Asks the viewer if they're sure and lets them optionally categorize why
 * (stored in user_history, not on the relationship row).
 *
 * On confirm, fires DELETE /user/relationship with the optional reason.
 * Caller is responsible for reacting to onConfirmed (navigating back,
 * invalidating the matches list, etc).
 */

import { useState } from "react";
import { Box, Button, chakra } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { UnmatchReason } from "@greenflagdate/shared";
import { useReq } from "@larner.dev/use-req";
import { apiReq } from "../../lib/apiReq";
import { Icon } from "../Icon/Icon";

const MChakraDiv = motion.create(chakra.div);

const modalVariants = {
  initial: { opacity: 0, scale: 0.92 },
  animate: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.18, ease: "easeOut" as const },
  },
  exit: {
    opacity: 0,
    scale: 0.96,
    transition: { duration: 0.12, ease: "easeIn" as const },
  },
};

const REASON_OPTIONS: { value: UnmatchReason; label: string }[] = [
  { value: UnmatchReason.MatchedByMistake, label: "Matched by mistake" },
  { value: UnmatchReason.BadInteraction, label: "Had a bad interaction" },
  { value: UnmatchReason.NoLongerInterested, label: "No longer interested" },
];

export interface UnmatchConfirmModalProps {
  userId: number;
  firstName: string;
  onClose: () => void;
  onConfirmed: () => void;
}

export function UnmatchConfirmModal({
  userId,
  firstName,
  onClose,
  onConfirmed,
}: UnmatchConfirmModalProps) {
  const [reason, setReason] = useState<UnmatchReason | null>(null);
  const [unmatchReq, unmatchReqState] = useReq<{ success: true }>(apiReq);

  async function handleConfirm() {
    const response = await unmatchReq.delete("/user/relationship", {
      userId,
      ...(reason ? { reason } : {}),
    });
    if (response.success) onConfirmed();
  }

  return (
    <Box
      position="fixed"
      inset="0"
      zIndex="40"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={{
        paddingTop: "calc(env(safe-area-inset-top) + 16px)",
        paddingBottom: "calc(env(safe-area-inset-bottom) + 16px)",
        paddingLeft: "calc(env(safe-area-inset-left) + 16px)",
        paddingRight: "calc(env(safe-area-inset-right) + 16px)",
      }}
    >
      <MChakraDiv
        aria-label="Close"
        onClick={onClose}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.15 }}
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(0,0,0,0.5)",
        }}
      />
      <MChakraDiv
        key="unmatch-confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="unmatch-confirm-title"
        initial="initial"
        animate="animate"
        exit="exit"
        variants={modalVariants}
        style={{
          position: "relative",
          width: "100%",
          maxWidth: "420px",
          background: "white",
          borderRadius: "20px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
          transformOrigin: "center",
        }}
      >
        <Box p="5">
          <chakra.h2
            id="unmatch-confirm-title"
            fontSize="lg"
            fontWeight="semibold"
            m="0"
            mb="1.5"
            color="fg.default"
          >
            Unmatch with {firstName}?
          </chakra.h2>
          <chakra.p fontSize="sm" color="fg.muted" m="0" mb="5">
            They won't show up in your matches anymore. Optionally tell us
            why — it helps us improve the app.
          </chakra.p>

          <Box display="flex" flexDirection="column" gap="2" mb="5">
            {REASON_OPTIONS.map((opt) => {
              const selected = reason === opt.value;
              return (
                <chakra.button
                  key={opt.value}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() =>
                    setReason(selected ? null : opt.value)
                  }
                  display="flex"
                  alignItems="center"
                  gap="3"
                  px="3.5"
                  py="3"
                  bg={selected ? "bg.brandSoft" : "bg.canvas"}
                  borderWidth="1px"
                  borderColor={selected ? "fg.brand" : "border.subtle"}
                  borderRadius="xl"
                  textAlign="left"
                  cursor="pointer"
                  color="fg.default"
                  fontSize="sm"
                  fontWeight="medium"
                  transition="background 120ms ease, border-color 120ms ease"
                >
                  <Box
                    w="18px"
                    h="18px"
                    borderRadius="full"
                    borderWidth="2px"
                    borderColor={selected ? "fg.brand" : "border.default"}
                    bg="bg.canvas"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    flexShrink={0}
                  >
                    {selected && (
                      <Box
                        w="8px"
                        h="8px"
                        borderRadius="full"
                        bg="fg.brand"
                      />
                    )}
                  </Box>
                  {opt.label}
                </chakra.button>
              );
            })}
          </Box>

          {unmatchReqState.error && (
            <chakra.p
              fontSize="sm"
              color="fg.danger"
              mb="4"
              role="alert"
            >
              {unmatchReqState.error.message}
            </chakra.p>
          )}

          <Box display="flex" gap="3">
            <Button
              flex="1"
              variant="outline"
              onClick={onClose}
              disabled={unmatchReqState.loading}
            >
              Cancel
            </Button>
            <Button
              flex="1"
              colorPalette="red"
              onClick={handleConfirm}
              loading={unmatchReqState.loading}
            >
              <Icon name="heart-broken" boxSize="14px" />
              Unmatch
            </Button>
          </Box>
        </Box>
      </MChakraDiv>
    </Box>
  );
}
